import { createSelector } from 'reselect';
import { RouteImportState } from 'src/graphql/types/delivery';
import { isDateBeforeCurrentWarehouseNightEnd } from 'src/utils/date';
import { DeliveryState, Mode } from '../slices/delivery';
import { RootState } from '../store';

export const deliverySelector: (state: RootState) => DeliveryState = (
  state: RootState,
) => state.delivery;

export const warehousesSelector = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.warehouses;
  },
);

export const warehousesLoadingSelector = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.warehousesLoading;
  },
);

export const routesSelector = createSelector(deliverySelector, (delivery) => {
  return delivery.routes;
});

export const nightEndSelector = createSelector(deliverySelector, (delivery) => {
  return delivery.nightEnd;
});

export const routesLoadingSelector = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.routesLoading;
  },
);

export const routesContextSelector = createSelector(
  deliverySelector,
  (delivery) => delivery.routesContext,
);

export const selectedWarehouseIdSelector = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.selectedWarehouseId;
  },
);

export const selectedRouteIdSelector = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.selectedRouteId;
  },
);

export const selectedCohortIdSelector = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.selectedCohortId;
  },
);

export const stopDetailsSelector = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.stopDetails;
  },
);

export const routeDetailsEditorSelector = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.routeDetailsEditor;
  },
);

export const locationCohortEditorSelector = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.locationCohortEditor;
  },
);

export const deliveryStopsReorderLoadingSelector = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.deliveryStopsReorderLoading;
  },
);

export const changeRouteEditorSelector = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.changeRouteEditor;
  },
);

export const selectedWarehouseSelector = createSelector(
  deliverySelector,
  (delivery) => {
    if (!delivery.selectedWarehouseId) {
      return undefined;
    }
    return delivery.warehouses?.find(
      (warehouse) => warehouse.id === delivery.selectedWarehouseId,
    );
  },
);

export const isNightEndCurrentSelector = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.nightEnd.viewing === delivery.nightEnd.current;
  },
);

export const primaryCardSelector = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.primaryCard;
  },
);

export const secondaryCardVisibleSelector = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.secondaryCardVisible;
  },
);

export const routeImportSelector = createSelector(
  deliverySelector,
  (delivery): RouteImportState => {
    return delivery.routeImport;
  },
);

// Route/stop editing is locked while any of these is in progress — so a run can't be raced by
// concurrent edits: automated routing (routeImport.isRunning), an observed manual run
// (manualRunRunning, from the poll), or a manual run this session just triggered
// (manualRunTriggering, the optimistic lock — covers etas, which has no polled running phase).
export const isRoutingLockedSelector = createSelector(
  deliverySelector,
  (delivery): boolean =>
    delivery.routeImport.isRunning ||
    delivery.manualRunRunning ||
    delivery.manualRunTriggering,
);

export const isSelectedNightEndBeforeWarehouseNightEnd = createSelector(
  deliverySelector,
  (delivery) => {
    const selectedDeliveryDay = delivery.nightEnd.viewing;
    if (selectedDeliveryDay === undefined) return false;
    return isDateBeforeCurrentWarehouseNightEnd(
      delivery,
      new Date(selectedDeliveryDay),
    );
  },
);

export const popoverSelector = createSelector(deliverySelector, (delivery) => {
  return delivery.popover;
});

export const deliveryStatusesSelector = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.deliveryStatuses;
  },
);

export const deliveryPrimaryStatusesSelector = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.deliveryPrimaryStatuses;
  },
);

export const deliveredQuantityEditorSelector = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.deliveredQuantityEditor;
  },
);

export const isRoutesSyncEnabled = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.isRoutesSyncEnabled;
  },
);

export const selectedOrderIdSelector = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.selectedOrderId;
  },
);

export const selectedRouteListTabSelector = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.selectedRouteListTab;
  },
);

export const lastThirdPartySubTabSelector = createSelector(
  deliverySelector,
  (delivery) => delivery.lastThirdPartySubTab,
);

export const thirdPartyOrdersSelector = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.thirdPartyOrders;
  },
);

export const thirdPartyOrdersLoadingSelector = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.thirdPartyOrdersLoading;
  },
);

export const isCohortViewSelector = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.mode === Mode.ViewCohorts;
  },
);

export const modeSelector = createSelector(deliverySelector, (delivery) => {
  return delivery.mode;
});

export const mapIconFiltersSelector = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.mapIconFilters;
  },
);

export const showDriverLocationTrailSelector = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.showDriverLocationTrail;
  },
);

export const driverLocationTrailSelector = createSelector(
  deliverySelector,
  (delivery) => {
    return delivery.driverLocationTrail;
  },
);
